import dayjs from 'utils/dayjs';
import ObjectUtils from '@spothero/utils/object';

import {reportingPost, spotheroGet} from 'api';
import {apiDateFormat, displayDateFormat} from 'hooks/config';
import {sendSentryException} from 'utils/error-handling';

/** @module api/accounting */

/**
 * Formats the request data for the accounting api endpoints
 *
 * @function
 * @param {object} data - The data to format
 * @return {TAccountingReportRequest}
 *
 * @memberof module:api/accounting/dto
 */
const formatAccountingReportRequest = (data) => {
    const formattedRequest = {
        page: data.page,
        /* eslint-disable camelcase */
        from_date: dayjs(data.fromDate, displayDateFormat).format(
            apiDateFormat
        ),
        to_date: dayjs(data.toDate, displayDateFormat).format(apiDateFormat),
        facility_ids: data.facilityIDs.toString(),
        /* eslint-enable camelcase */
    };

    return formattedRequest;
};

/**
 * Gets a collection of accounting data for a given set of facilities
 *
 * @function
 * @param {TAccountingReportRequest} data - The parameters for the report
 * @param {object} config - The axios config
 *
 * @see '/control-panel-service/v1/accounting/facilities/breakdown/
 * @return {Promise<TAccountingFacilityBreakdownReportResponse[]>}
 */
export const getFacilitiesBreakdownReport = async (data, config = {}) => {
    try {
        const formattedRequest = formatAccountingReportRequest(data);

        const response = await reportingPost(
            'accounting/facilities/breakdown/',
            formattedRequest,
            config
        );

        return ObjectUtils.camelizeKeys(response.data);
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets the accounting summary data for a given collection of facilities
 *
 * @function
 * @param {TAccountingReportRequest} data - The parameters for the report
 * @param {Object} [config] - The axios config
 * @return {Promise<TAccountingSummaryReportReponse>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/accounting/summary/'
 */
export const getAccountingSummaryReport = async ({...data}, config = {}) => {
    try {
        const formattedRequest = formatAccountingReportRequest(data);

        const response = await reportingPost(
            'accounting/summary/',
            formattedRequest,
            config
        );

        return ObjectUtils.camelizeKeys(response.data);
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets the accounting summary data for a given collection of facilities
 *
 * @function
 * @param {number|string} id - The parameters for the report
 * @return {Promise<TReservationAccountingDetailsReponse>}
 * @throws {Error}
 *
 * @see '/api/v1/reservations/{id}/accounting-details/'
 */
export const getReservationDetails = async (id) => {
    try {
        const response = await spotheroGet(
            `/reservations/${id}/accounting-details/`
        );

        return response.data;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
